import { useEffect } from 'react';
import { Layout } from './components/Layout';
import { useAutoSave } from './hooks/useAutoSave';
import { useAppStore, recalculateStore } from './store/useAppStore';
import { loadFromLocalStorage } from './store/persistence';

const SCHEMA_VERSION = 1;

/**
 * Validate that persisted data matches the expected schema version.
 * Returns null if the version is incompatible or data is corrupt.
 */
function validatePersistedData(data: unknown): { courses: unknown; settings: unknown } | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  // If there's an explicit version field, check it
  if ('version' in obj && typeof obj.version === 'number' && obj.version > SCHEMA_VERSION) {
    console.warn(`Persisted data version ${obj.version} exceeds expected ${SCHEMA_VERSION} — skipping load`);
    return null;
  }
  return data as { courses: unknown; settings: unknown };
}

export function App() {
  useAutoSave();

  // Load persisted state on mount with schema validation
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const saved = await loadFromLocalStorage();
      if (cancelled) return;
      if (saved) {
        const validated = validatePersistedData(saved);
        if (validated) {
          useAppStore.getState().importCourses(saved.courses);
          useAppStore.getState().loadSettings(saved.settings);
          if (saved.collegeShortlist && saved.collegeShortlist.length > 0) {
            useAppStore.setState({ collegeShortlist: saved.collegeShortlist });
          }
        } else {
          console.warn('Persisted data failed validation — starting fresh');
        }
      }
      recalculateStore();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <Layout />;
}
